import { PureComponent } from "react";
import { Badge, BadgeContainer } from "@progress/kendo-react-indicators";

import type { BadgeThemeColor } from "@progress/kendo-react-indicators";

import "./Icon.scss";

// Font Awesome 5 Pro
export const IconType = {
  Alert: "Alert",
  AlertNew: "AlertNew",
  ApplyMark: "ApplyMark",
  ArrowRight: "ArrowRight",
  Bars: "Bars",
  BookCodebooks: "BookCodebooks",
  BookReferences: "BookReferences",
  Building: "Building",
  CancelMark: "CancelMark",
  CheckCircle: "CheckCircle",
  CheckMark: "CheckMark",
  ChevronDown: "ChevronDown",
  ChevronRight: "ChevronRight",
  CircleNotch: "CircleNotch",
  CircleNotchSpin: "CircleNotchSpin",
  CodingAdvice: "CodingAdvice",
  Copy: "Copy",
  Debug: "Debug",
  Dots: "Dots",
  Edit: "Edit",
  Envelope: "Envelope",
  ExternalLink: "ExternalLink",
  Error: "Error",
  ErrorAlt: "ErrorAlt",
  ErrorExclamation: "ErrorExclamation",
  Export: "Export",
  Facilities: "Facilities",
  FileFromTemplate: "FileFromTemplate",
  FilePlus: "FilePlus",
  Help: "Help",
  HelpAlt: "HelpAlt",
  History: "History",
  Home: "Home",
  Import: "Import",
  Info: "Info",
  InfoAlt: "InfoAlt",
  InfoFilled: "InfoFilled",
  Logout: "Logout",
  LongArrowUp: "LongArrowUp",
  Pencil: "Pencil",
  Plus: "Plus",
  PlusCircle: "PlusCircle",
  POAExempt: "POAExempt",
  Practice: "Practice",
  Search: "Search",
  Settings: "Settings",
  SolidCircle: "SolidCircle",
  SolidSquare: "SolidSquare",
  SolidTriangle: "SolidTriangle",
  Trash: "Trash",
  User: "User",
  UserPlus: "UserPlus",
  Users: "Users",
  UserSlash: "UserSlash",
  Video: "Video",
  Warning: "Warning",
  WarningAlt: "WarningAlt",
  Wrench: "Wrench",
  Inbox: "Inbox",
  FileExcel: "FileExcel",
  Refresh: "Refresh",
} as const;

export type IconTypeValue = (typeof IconType)[keyof typeof IconType];

interface IconProps {
  type?: IconTypeValue;
  className?: string;
  badgeCounter?: number;
  title?: string;
  forceTooltip?: boolean;
  setRef?: (ref: HTMLElement) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const VerticalDotsClass = "far fa-ellipsis-v";

export class Icon extends PureComponent<IconProps> {
  static layeredCounter(iconClasses: string, _colorClass?: BadgeThemeColor, count?: number) {
    return (
      <BadgeContainer>
        <i className={iconClasses} />
        <Badge themeColor="inherit">{count}</Badge>
      </BadgeContainer>
    );
  }

  render() {
    const { badgeCounter, title, forceTooltip, className, type, setRef, ...otherProps } =
      this.props;

    let classes = className ? ["fa-fw icon", className] : ["fa-fw icon"];
    if (title || forceTooltip) {
      classes.push("with-tooltip");
    }
    switch (type) {
      case IconType.FileExcel:
        classes = classes.concat(["fal", "fa-file-excel", "fa-regular"]);
        break;
      case IconType.ApplyMark:
        classes = classes.concat(["fal", "fa-check"]);
        break;
      case IconType.ArrowRight:
        classes = classes.concat(["fal", "fa-arrow-right"]);
        break;
      case IconType.CancelMark:
        classes = classes.concat(["fal", "fa-times"]);
        break;
      case IconType.CheckCircle:
        classes = classes.concat(["fas", "fa-check-circle"]);
        break;
      case IconType.CodingAdvice:
        classes = classes.concat(["fal", "fa-copyright"]);
        break;
      case IconType.CircleNotch:
        classes = classes.concat(["fal", "fa-circle-notch"]);
        break;
      case IconType.CircleNotchSpin:
        classes = classes.concat(["fal", "fa-circle-notch", "fa-spin"]);
        break;
      case IconType.Copy:
        classes = classes.concat(["fal", "fa-copy"]);
        break;
      case IconType.Dots:
        classes = classes.concat(["fal", "fa-ellipsis-h"]);
        break;
      case IconType.Edit:
        classes = classes.concat(["fal", "fa-edit"]);
        break;
      case IconType.Envelope:
        classes = classes.concat(["fas", "fa-envelope"]);
        break;
      case IconType.Error:
        classes = classes.concat(["error-icon", "fas", "fa-times-circle"]);
        break;
      case IconType.ErrorAlt:
        classes = classes.concat(["fal", "fa-times-square"]);
        break;
      case IconType.ErrorExclamation:
        classes = classes.concat(["error-icon", "fas", "fa-exclamation-circle"]);
        break;
      case IconType.Export:
        classes = classes.concat(["fal", "fa-upload"]);
        break;
      case IconType.FilePlus:
        classes = classes.concat(["fal", "fa-file-plus"]);
        break;
      case IconType.FileFromTemplate:
        classes = classes.concat(["fal", "fa-files-medical"]);
        break;
      case IconType.Home:
        classes = classes.concat(["fal", "fa-home"]);
        break;
      case IconType.Help:
        classes = classes.concat(["fal", "fa-question-circle"]);
        break;
      case IconType.HelpAlt:
        classes = classes.concat(["fal", "fa-question"]);
        break;
      case IconType.Import:
        classes = classes.concat(["fal", "fa-download"]);
        break;
      case IconType.Info:
        classes = classes.concat(["fal", "fa-info-circle"]);
        break;
      case IconType.InfoFilled:
        classes = classes.concat(["fa", "fa-info-circle"]);
        break;
      case IconType.InfoAlt:
        classes = classes.concat(["fal", "fa-info"]);
        break;
      case IconType.LongArrowUp:
        classes = classes.concat(["fal", "fa-long-arrow-up"]);
        break;
      case IconType.Pencil:
        classes = classes.concat(["fal", "fa-pencil"]);
        break;
      case IconType.Plus:
        classes = classes.concat(["far", "fa-plus"]);
        break;
      case IconType.PlusCircle:
        classes = classes.concat(["fal", "fa-plus-circle"]);
        break;
      case IconType.Search:
        classes = classes.concat(["fal", "fa-search"]);
        break;
      case IconType.Settings:
        classes = classes.concat(["fal", "fa-cog"]);
        break;
      case IconType.Logout:
        classes = classes.concat(["fal", "fa-sign-out"]);
        break;
      case IconType.Debug:
        classes = classes.concat(["fal", "fa-bug"]);
        break;
      case IconType.Building:
        classes = classes.concat(["fal", "fa-building"]);
        break;
      case IconType.Wrench:
        classes = classes.concat(["fal", "fa-wrench"]);
        break;
      case IconType.Users:
        classes = classes.concat(["fal", "fa-users"]);
        break;
      case IconType.POAExempt:
        classes = classes.concat(["fas", "fa-parking-circle-slash"]);
        break;
      case IconType.Alert:
        return Icon.layeredCounter("fal fa-bell", "error", badgeCounter);
      case IconType.AlertNew:
        classes = classes.concat(["fal", "fa-bell"]);
        break;
      case IconType.BookReferences:
        classes = classes.concat(["fal", "fa-book-alt"]);
        break;
      case IconType.BookCodebooks:
        classes = classes.concat(["fal", "fa-book-alt"]);
        break;
      case IconType.User:
        classes = classes.concat(["fal", "fa-user"]);
        break;
      case IconType.ChevronDown:
        classes = classes.concat(["fal", "fa-chevron-down"]);
        break;
      case IconType.ChevronRight:
        classes = classes.concat(["fal", "fa-chevron-right"]);
        break;
      case IconType.UserPlus:
        classes = classes.concat(["fal", "fa-user-plus"]);
        break;
      case IconType.Bars:
        classes = classes.concat(["fas", "fa-bars"]);
        break;
      case IconType.ExternalLink:
        classes = classes.concat(["far", "fa-external-link"]);
        break;
      case IconType.History:
        return Icon.layeredCounter("fal fa-history", "error", badgeCounter);
      case IconType.Trash:
        classes = classes.concat(["fal", "fa-trash-alt"]);
        break;
      case IconType.SolidCircle:
        classes = classes.concat(["fas", "fa-solid", "fa-circle"]);
        break;
      case IconType.SolidSquare:
        classes = classes.concat(["fas", "fa-solid", "fa-square"]);
        break;
      case IconType.SolidTriangle:
        classes = classes.concat(["fas", "fa-solid", "fa-triangle"]);
        break;
      case IconType.UserSlash:
        classes = classes.concat(["fas", "fa-user-slash"]);
        break;
      case IconType.Video:
        classes = classes.concat(["fas", "fa-video"]);
        break;
      case IconType.Inbox:
        classes = classes.concat(["fas", "fa-inbox"]);
        break;
      case IconType.CheckMark:
        classes = classes.concat(["fal", "fa-check"]);
        break;
      case IconType.WarningAlt:
        classes = classes.concat(["warning-icon", "fas", "fa-exclamation-triangle"]);
        break;
      case IconType.Refresh:
        classes = classes.concat(["fa", "fa-rotate-right"]);
        break;
      case IconType.Facilities:
        classes = classes.concat(["fal", "fa-hospital"]);
        break;
      case IconType.Practice:
        classes = classes.concat(["fal", "fa-building"]);
        break;
      case IconType.Warning:
      default:
        classes = classes.concat(["warning-icon", "fal", "fa-exclamation-triangle"]);
        break;
    }
    return title || forceTooltip ?
        <i className={classes.join(" ")} title={title ?? "‎ "} ref={setRef} {...otherProps} />
      : <i className={classes.join(" ")} ref={setRef} {...otherProps} />;
  }
}
